import React, { useEffect, useMemo, useState } from 'react';
import PettyCashService from '../../services/PettyCashService';
import BranchService from '../../services/BranchService';
import StaffService from '../../services/StaffService';
import MemberService from '../../services/MemberService';
import VendorService from '../../services/VendorService';
import ProjectService from '../../services/ProjectService';
import ExpenditureTypeService from '../../services/ExpenditureTypeService';
import PayoutService from '../../services/PayoutService';
import StorageService from '../../services/StorageService';
import utility from '../../utils/utility';
import ReactPaginate from 'react-paginate';
import FilterBar from '../../components/FilterBar';
import { filterList } from '../../utils/filterUtils';

const PettyCash = () => {
    const [pettyCashList, setPettyCashList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [pageCount, setPageCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;
    const [searchText, setSearchText] = useState('');
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');
    const [filterBranch, setFilterBranch] = useState('');
    const [filterProject, setFilterProject] = useState('');
    const [filterVendor, setFilterVendor] = useState('');
    const [filterPaymentMode, setFilterPaymentMode] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [bulkBranchId, setBulkBranchId] = useState('');
    const [bulkProjectId, setBulkProjectId] = useState('');
    const [bulkFormData, setBulkFormData] = useState([{
        entityType: 'staff',
        staffId: '',
        memberId: '',
        vendorId: '',
        expenditureTypeId: '',
        paymentDate: new Date().toISOString().split('T')[0],
        amount: '',
        paymentMode: 'Cash',
        transactionNo: '',
        remarks: ''
    }]);

    const handleFilterChange = (setter) => (event) => {
        setter(event.target.value);
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setSearchText('');
        setFilterDateFrom('');
        setFilterDateTo('');
        setFilterBranch('');
        setFilterProject('');
        setFilterVendor('');
        setFilterPaymentMode('');
        setFilterStatus('');
        setCurrentPage(1);
    };

    // Dropdown data
    const [branches, setBranches] = useState([]);
    const [staffs, setStaffs] = useState([]);
    const [members, setMembers] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [projects, setProjects] = useState([]);
    const [expenditureTypes, setExpenditureTypes] = useState([]);

    const [formData, setFormData] = useState({
        id: '',
        branchId: '',
        entityType: 'staff', // staff, member, vendor
        staffId: '',
        memberId: '',
        vendorId: '',
        projectId: '',
        expenditureTypeId: '',
        paymentDate: '',
        amount: '',
        paymentMode: 'Cash',
        transactionNo: '',
        remarks: '',
        transactionImage: '',
        status: 'completed',
        isActive: 1
    });
    const filteredPettyCash = useMemo(() => pettyCashList.filter((cash) => {
        if (!searchText) return true;
        return filterList([cash], searchText, ['paymentMode', 'transactionNo', 'projectName', 'branchName', 'status', 'memberName', 'staffName', 'vendorName']).length > 0;
    }), [pettyCashList, searchText]);

    const loggedInStaffId = StorageService.loginData()?.employeeId;
    const [staffPayoutAmount, setStaffPayoutAmount] = useState(0);
    const [totalPayoutAmount, setTotalPayoutAmount] = useState(0);
    const [totalPettyCashAmount, setTotalPettyCashAmount] = useState(0);
    const [totalStaffPettyCashAmount, setTotalStaffPettyCashAmount] = useState(0);

    const formatCurrency = (value) => Number(value || 0).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

    useEffect(() => {
        const fetchStaffPayout = async () => {
            if (!loggedInStaffId) {
                setStaffPayoutAmount(0);
                setTotalPayoutAmount(0);
                return;
            }

            try {
                const payoutData = await PayoutService.getAllPayouts(1, 1000, { staffId: loggedInStaffId, projectId: filterProject });
                const totalPayoutData = await PayoutService.getAllPayouts(1, 1000, { projectId: filterProject });
                const total = (payoutData.data || []).reduce((sum, item) => sum + Number(item.amount || 0), 0);
                const totalPayoutAmount = (totalPayoutData.data || []).reduce((sum, item) => sum + Number(item.amount || 0), 0);
                setStaffPayoutAmount(total);
                setTotalPayoutAmount(totalPayoutAmount);
            } catch (err) {
                console.error('Failed to fetch staff payouts', err);
                setStaffPayoutAmount(0);
                setTotalPayoutAmount(0);
            }
        };

        fetchStaffPayout();
    }, [loggedInStaffId, filterProject]);

    const remainingBalance = useMemo(() => totalPayoutAmount - totalPettyCashAmount, [totalPayoutAmount, totalPettyCashAmount]);
    const remainingStaffBalance = useMemo(() => staffPayoutAmount - totalStaffPettyCashAmount, [staffPayoutAmount, totalStaffPettyCashAmount]);

    const fetchPettyCashStats = async () => {
        try {
            const stats = await PettyCashService.getPettyCashStats({
                branchId: filterBranch,
                projectId: filterProject,
                vendorId: filterVendor,
                paymentMode: filterPaymentMode,
                status: filterStatus,
                startDate: filterDateFrom,
                endDate: filterDateTo
            });
            const staffStats = await PettyCashService.getPettyCashStats({
                createdBy: loggedInStaffId,
                branchId: filterBranch,
                projectId: filterProject,
                vendorId: filterVendor,
                paymentMode: filterPaymentMode,
                status: filterStatus,
                startDate: filterDateFrom,
                endDate: filterDateTo
            });
            setTotalPettyCashAmount(stats.data?.totalAmount || 0);
            setTotalStaffPettyCashAmount(staffStats.data?.totalAmount || 0);
        } catch (err) {
            console.error('Failed to fetch petty cash stats', err);
            setTotalPettyCashAmount(0);
            setTotalStaffPettyCashAmount(0);
        }
    };

    useEffect(() => {
        fetchDropdownData();
    }, []);

    useEffect(() => {
        fetchPettyCashStats();
    }, [filterDateFrom, filterDateTo, filterBranch, filterProject, filterVendor, filterPaymentMode, filterStatus]);

    useEffect(() => {
        fetchPettyCash();
    }, [currentPage, filterDateFrom, filterDateTo, filterBranch, filterProject, filterVendor, filterPaymentMode, filterStatus]);

    const fetchPettyCash = async () => {
        try {
            setLoading(true);
            const data = await PettyCashService.getAllPettyCash(currentPage, itemsPerPage, {
                branchId: filterBranch,
                projectId: filterProject,
                vendorId: filterVendor,
                paymentMode: filterPaymentMode,
                status: filterStatus,
                startDate: filterDateFrom,
                endDate: filterDateTo
            });
            setPettyCashList(data.data || []);
            setPageCount(data.pagination?.totalPages || 0);
        } catch (err) {
            utility.showError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchDropdownData = async () => {
        try {
            const [branchRes, staffRes, memberRes, vendorRes, projectRes, expRes] = await Promise.all([
                BranchService.getAllBranches(1, 100),
                StaffService.getAll(1, 100),
                MemberService.getAll(1, 100),
                VendorService.getAll(1, 100),
                ProjectService.getAll(1, 100),
                ExpenditureTypeService.getAll(1, 100)
            ]);
            setBranches(branchRes.data || []);
            setStaffs(staffRes.data || []);
            setMembers(memberRes.data || []);
            setVendors(vendorRes.data || []);
            setProjects(projectRes.data || []);
            setExpenditureTypes(expRes.data || []);
        } catch (err) {
            console.error('Failed to fetch dropdown data', err);
        }
    };

    const handleOpenModal = (item = null) => {
        if (item) {
            setEditMode(true);
            let entityType = 'staff';
            if (item.memberId) entityType = 'member';
            else if (item.vendorId) entityType = 'vendor';

            setFormData({
                id: item.id,
                branchId: item.branchId || '',
                entityType: entityType,
                staffId: item.staffId || '',
                memberId: item.memberId || '',
                vendorId: item.vendorId || '',
                projectId: item.projectId || '',
                expenditureTypeId: item.expenditureTypeId || '',
                paymentDate: item.paymentDate ? item.paymentDate.split('T')[0] : '',
                amount: item.amount || '',
                paymentMode: item.paymentMode || 'Cash',
                transactionNo: item.transactionNo || '',
                remarks: item.remarks || '',
                transactionImage: item.transactionImage || '',
                status: item.status || 'completed',
                isActive: item.isActive
            });
        } else {
            setEditMode(false);
            setFormData({
                id: '',
                branchId: '',
                entityType: 'staff',
                staffId: '',
                memberId: '',
                vendorId: '',
                projectId: '',
                expenditureTypeId: '',
                paymentDate: new Date().toISOString().split('T')[0],
                amount: '',
                paymentMode: 'Cash',
                transactionNo: '',
                remarks: '',
                transactionImage: '',
                status: 'completed',
                isActive: 1
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'isActive' ? parseInt(value) : value,
            ...(name === 'entityType' ? { staffId: '', memberId: '', vendorId: '' } : {})
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            const payload = { ...formData };
            if (formData.entityType === 'staff') { payload.memberId = null; payload.vendorId = null; }
            else if (formData.entityType === 'member') { payload.staffId = null; payload.vendorId = null; }
            else if (formData.entityType === 'vendor') { payload.staffId = null; payload.memberId = null; }

            if (editMode) {
                delete payload.entityType;
                await PettyCashService.updatePettyCash(formData.id, payload);
                utility.showSuccess('Petty cash transaction updated successfully');
            } else {
                await PettyCashService.createPettyCash(payload);
                utility.showSuccess('Petty cash transaction created successfully');
            }
            handleCloseModal();
            fetchPettyCash();
        } catch (err) {
            utility.showError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleBulkOpenModal = () => {
        setBulkBranchId('');
        setBulkProjectId('');
        setBulkFormData([{
            entityType: 'staff',
            staffId: '',
            memberId: '',
            vendorId: '',
            expenditureTypeId: '',
            paymentDate: new Date().toISOString().split('T')[0],
            amount: '',
            paymentMode: 'Cash',
            transactionNo: '',
            remarks: ''
        }]);
        setShowBulkModal(true);
    };

    const handleBulkCloseModal = () => {
        setShowBulkModal(false);
        setBulkBranchId('');
        setBulkProjectId('');
    };

    const handleBulkInputChange = (index, field, value) => {
        const updatedBulkData = [...bulkFormData];
        updatedBulkData[index][field] = value;
        if (field === 'entityType') {
            updatedBulkData[index]['staffId'] = '';
            updatedBulkData[index]['memberId'] = '';
            updatedBulkData[index]['vendorId'] = '';
        }
        setBulkFormData(updatedBulkData);
    };

    const handleAddBulkRow = () => {
        setBulkFormData([...bulkFormData, {
            entityType: 'staff',
            staffId: '',
            memberId: '',
            vendorId: '',
            expenditureTypeId: '',
            paymentDate: new Date().toISOString().split('T')[0],
            amount: '',
            paymentMode: 'Cash',
            transactionNo: '',
            remarks: ''
        }]);
    };

    const handleRemoveBulkRow = (index) => {
        if (bulkFormData.length > 1) {
            setBulkFormData(bulkFormData.filter((_, i) => i !== index));
        }
    };

    const handleBulkSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            const payload = bulkFormData.map(tx => ({
                ...tx,
                branchId: bulkBranchId,
                projectId: bulkProjectId || null
            }));
            await PettyCashService.createBulkPettyCash(payload);
            utility.showSuccess(`${bulkFormData.length} petty cash transactions created successfully`);
            handleBulkCloseModal();
            fetchPettyCash();
            fetchPettyCashStats();
        } catch (err) {
            utility.showError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleToggleStatus = async (item) => {
        try {
            const newStatus = item.isActive ? 0 : 1;
            await PettyCashService.updateStatus(item.id, newStatus);
            utility.showSuccess(`Petty cash status ${newStatus ? 'activated' : 'deactivated'} successfully`);
            fetchPettyCash();
        } catch (err) {
            utility.showError(err.message);
        }
    };

    const handlePageClick = (event) => {
        setCurrentPage(event.selected + 1);
    };

    return (
        <div className="container-fluid py-2">
            <div className="row">
                <div className="col-12 text-end mb-3">
                    <button className="btn bg-gradient-dark mb-0 me-2" onClick={() => handleOpenModal()}>
                        <i className="material-symbols-rounded text-sm">add</i>&nbsp;&nbsp;Add New Petty Cash
                    </button>
                    <button className="btn bg-gradient-info mb-0" onClick={handleBulkOpenModal}>
                        <i className="material-symbols-rounded text-sm">playlist_add</i>&nbsp;&nbsp;Add Multiple
                    </button>
                </div>
                <div className="col-12">
                    <div className="card my-4">
                        <div className="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
                            <div className="bg-gradient-dark shadow-dark border-radius-lg pt-4 pb-3">
                                <h6 className="text-white text-capitalize ps-3">Petty Cash Transactions</h6>
                            </div>
                        </div>
                        <div className="card-body px-0 pb-2">
                            <div className="px-3 mb-3">
                                <div className="bg-white rounded-3 shadow-sm p-3">
                                    <div className="row gy-2 gx-2 align-items-end">
                                        <div className="col-lg-3 col-md-6">
                                            <FilterBar
                                                value={searchText}
                                                onChange={setSearchText}
                                                onClear={() => setSearchText('')}
                                                placeholder="Search petty cash..."
                                            />
                                        </div>

                                        <div className="col-lg-2 col-md-6">
                                            <label className="form-label text-muted small">Date From</label>
                                            <input type="date" className="form-control" value={filterDateFrom} onChange={handleFilterChange(setFilterDateFrom)} />
                                        </div>
                                        <div className="col-lg-2 col-md-6">
                                            <label className="form-label text-muted small">Date To</label>
                                            <input type="date" className="form-control" value={filterDateTo} onChange={handleFilterChange(setFilterDateTo)} />
                                        </div>
                                        <div className="col-lg-2 col-md-6">
                                            <label className="form-label text-muted small">Branch</label>
                                            <select className="form-select" value={filterBranch} onChange={handleFilterChange(setFilterBranch)}>
                                                <option value="">All Branches</option>
                                                {branches.map((branch) => (
                                                    <option key={branch.id} value={branch.id}>{branch.branchName}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label text-muted small">Project</label>
                                            <select className="form-select" value={filterProject} onChange={handleFilterChange(setFilterProject)}>
                                                <option value="">All Projects</option>
                                                {projects.map((project) => (
                                                    <option key={project.id} value={project.id}>{project.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label text-muted small">Vendor</label>
                                            <select className="form-select" value={filterVendor} onChange={handleFilterChange(setFilterVendor)}>
                                                <option value="">All Vendors</option>
                                                {vendors.map((vendor) => (
                                                    <option key={vendor.id} value={vendor.id}>{vendor.vendorName}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-1">
                                            <label className="form-label text-muted small">Mode</label>
                                            <select className="form-select" value={filterPaymentMode} onChange={handleFilterChange(setFilterPaymentMode)}>
                                                <option value="">Any</option>
                                                <option value="Cash">Cash</option>
                                                <option value="UPI">UPI</option>
                                                <option value="NEFT">NEFT</option>
                                                <option value="RTGS">RTGS</option>
                                            </select>
                                        </div>
                                        <div className="col-md-1">
                                            <label className="form-label text-muted small">Status</label>
                                            <select className="form-select" value={filterStatus} onChange={handleFilterChange(setFilterStatus)}>
                                                <option value="">Any</option>
                                                <option value="completed">Completed</option>
                                                <option value="pending">Pending</option>
                                                <option value="cancelled">Cancelled</option>
                                            </select>
                                        </div>
                                        <div className="col-lg-2 col-md-6 d-flex align-items-end">
                                            <button type="button" className="btn btn-outline-secondary w-100" onClick={resetFilters}>
                                                Clear Filters
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="px-3 mb-3">
                                <div className="row gx-2 gy-2">
                                    <div className="col-md-4">
                                        <div className="bg-white rounded-3 shadow-sm p-3 h-100">
                                            <p className="text-muted text-uppercase text-xs mb-1">Total payout</p>
                                            <h5 className="mb-0">₹{formatCurrency(totalPayoutAmount)}</h5>
                                            <small className="text-secondary">Amount paid </small>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="bg-white rounded-3 shadow-sm p-3 h-100">
                                            <p className="text-muted text-uppercase text-xs mb-1">Total petty cash</p>
                                            <h5 className="mb-0">₹{formatCurrency(totalPettyCashAmount)}</h5>
                                            <small className="text-secondary">Total amount spent in petty cash</small>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="bg-white rounded-3 shadow-sm p-3 h-100">
                                            <p className="text-muted text-uppercase text-xs mb-1">Remaining balance</p>
                                            <h5 className={`mb-0 ${remainingBalance >= 0 ? 'text-success' : 'text-danger'}`}>
                                                ₹{formatCurrency(remainingBalance)}
                                            </h5>
                                            <small className="text-secondary">Total minus your payout</small>
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <div className="bg-white rounded-3 shadow-sm p-3 h-100">
                                            <p className="text-muted text-uppercase text-xs mb-1">Your payout</p>
                                            <h5 className="mb-0">₹{formatCurrency(staffPayoutAmount)}</h5>
                                            <small className="text-secondary">Amount paid to your staff ID</small>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="bg-white rounded-3 shadow-sm p-3 h-100">
                                            <p className="text-muted text-uppercase text-xs mb-1">Your Total petty cash</p>
                                            <h5 className="mb-0">₹{formatCurrency(totalStaffPettyCashAmount)}</h5>
                                            <small className="text-secondary">Total amount spent in petty cash</small>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="bg-white rounded-3 shadow-sm p-3 h-100">
                                            <p className="text-muted text-uppercase text-xs mb-1">Your Remaining balance</p>
                                            <h5 className={`mb-0 ${remainingStaffBalance >= 0 ? 'text-success' : 'text-danger'}`}>
                                                ₹{formatCurrency(remainingStaffBalance)}
                                            </h5>
                                            <small className="text-secondary">Total minus your payout</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="table-responsive p-0">
                                {loading ? (
                                    <div className="p-4 text-center">Loading...</div>
                                ) : (
                                    <table className="table table-bordered align-items-center mb-0">
                                        <thead>
                                            <tr>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Date</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Paid By</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Paid to Entity / Project</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Exp. Type</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Mode</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-end">Amount (₹)</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Status</th>
                                                <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredPettyCash.length === 0 ? (
                                                <tr>
                                                    <td colSpan="7" className="text-center py-4">No petty cash transactions found.</td>
                                                </tr>
                                            ) : (
                                                filteredPettyCash.map((item) => (
                                                    <tr key={item.id}>
                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0 px-3">{item.paymentDate ? item.paymentDate.split('T')[0] : 'N/A'}</p>
                                                        </td>
                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0">
                                                                {item.createdByName || 'N/A'}
                                                            </p>

                                                        </td>

                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0">
                                                                {item.staffName || item.memberName || item.vendorName || 'N/A'}
                                                            </p>
                                                            <p className="text-xs text-secondary mb-0">{item.projectName || 'No Project'}</p>
                                                        </td>
                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0">{item.expenditureTypeName || 'N/A'}</p>
                                                            <p className="text-xs text-secondary mb-0">{item.remarks || ''}</p>
                                                        </td>
                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0">{item.paymentMode || 'N/A'}</p>
                                                            <p className="text-xs text-secondary mb-0">{item.transactionNo || ''}</p>
                                                        </td>
                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0 text-end">{item.amount || '0.00'}</p>
                                                        </td>
                                                        <td>
                                                            <span className={`badge badge-sm bg-gradient-${item.isActive ? 'success' : 'secondary'} cursor-pointer`} onClick={() => handleToggleStatus(item)}>
                                                                {item.isActive ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </td>
                                                        <td className="align-middle text-center">
                                                            <button className="btn btn-link text-info mb-0" onClick={() => handleOpenModal(item)}>
                                                                Edit
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colSpan="7">
                                                    <ReactPaginate
                                                        forcePage={currentPage - 1}
                                                        breakLabel="..."
                                                        nextLabel=">"
                                                        onPageChange={handlePageClick}
                                                        pageRangeDisplayed={3}
                                                        marginPagesDisplayed={2}
                                                        pageCount={pageCount}
                                                        previousLabel="<"
                                                        containerClassName="pagination px-3"
                                                        pageClassName="page-item"
                                                        pageLinkClassName="page-link"
                                                        previousClassName="page-item"
                                                        previousLinkClassName="page-link"
                                                        nextClassName="page-item"
                                                        nextLinkClassName="page-link"
                                                        activeClassName="active"
                                                        disabledClassName="disabled"
                                                        breakClassName="page-item"
                                                        breakLinkClassName="page-link"
                                                    />
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header border-0">
                                <h5 className="modal-title font-weight-normal">{editMode ? 'Edit Petty Cash' : 'Add New Petty Cash'}</h5>
                                <button type="button" className="btn-close text-dark" onClick={handleCloseModal}>
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body row">
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Branch</label>
                                            <select name="branchId" className="form-control" value={formData.branchId} onChange={handleInputChange} required>
                                                <option value="">Select Branch</option>
                                                {branches.map(b => <option key={b.id} value={b.id}>{b.branchName}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Project</label>
                                            <select name="projectId" className="form-control" value={formData.projectId} onChange={handleInputChange}>
                                                <option value="">Select Project</option>
                                                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-4 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Entity Type</label>
                                            <select name="entityType" className="form-control" value={formData.entityType} onChange={handleInputChange}>
                                                <option value="staff">Staff</option>
                                                <option value="member">Member</option>
                                                <option value="vendor">Vendor</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-8 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Select {formData.entityType.charAt(0).toUpperCase() + formData.entityType.slice(1)}</label>
                                            {formData.entityType === 'staff' && (
                                                <select name="staffId" className="form-control" value={formData.staffId} onChange={handleInputChange} required>
                                                    <option value="">Select Staff</option>
                                                    {staffs.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName} ({s.staffCode})</option>)}
                                                </select>
                                            )}
                                            {formData.entityType === 'member' && (
                                                <select name="memberId" className="form-control" value={formData.memberId} onChange={handleInputChange} required>
                                                    <option value="">Select Member</option>
                                                    {members.map(m => <option key={m.id} value={m.id}>{m.memberName}</option>)}
                                                </select>
                                            )}
                                            {formData.entityType === 'vendor' && (
                                                <select name="vendorId" className="form-control" value={formData.vendorId} onChange={handleInputChange} required>
                                                    <option value="">Select Vendor</option>
                                                    {vendors.map(v => <option key={v.id} value={v.id}>{v.vendorName}</option>)}
                                                </select>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Expenditure Type</label>
                                            <select name="expenditureTypeId" className="form-control" value={formData.expenditureTypeId} onChange={handleInputChange} required>
                                                <option value="">Select Type</option>
                                                {expenditureTypes.map(t => <option key={t.id} value={t.id}>{t.expenditureTypeName}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Payment Date</label>
                                            <input type="date" name="paymentDate" className="form-control" value={formData.paymentDate} onChange={handleInputChange} required />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Amount (₹)</label>
                                            <input type="number" name="amount" className="form-control" value={formData.amount} onChange={handleInputChange} required />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Payment Mode</label>
                                            <select name="paymentMode" className="form-control" value={formData.paymentMode} onChange={handleInputChange}>
                                                <option value="Cash">Cash</option>
                                                <option value="Bank Transfer">Bank Transfer</option>
                                                <option value="UPI">UPI</option>
                                                <option value="Cheque">Cheque</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Transaction No</label>
                                            <input type="text" name="transactionNo" className="form-control" value={formData.transactionNo} onChange={handleInputChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Remarks</label>
                                            <input type="text" name="remarks" className="form-control" value={formData.remarks} onChange={handleInputChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Is Active</label>
                                            <select name="isActive" className="form-control" value={formData.isActive} onChange={handleInputChange}>
                                                <option value={1}>Active</option>
                                                <option value={0}>Inactive</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer border-0">
                                    <button type="button" className="btn bg-gradient-secondary" onClick={handleCloseModal} disabled={saving}>Cancel</button>
                                    <button type="submit" className="btn bg-gradient-dark" disabled={saving}>
                                        {saving ? 'Saving...' : (editMode ? 'Update' : 'Save')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Modal */}
            {showBulkModal && (
                <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-xl" role="document" style={{ maxWidth: '90%' }}>
                        <div className="modal-content">
                            <div className="modal-header border-0">
                                <h5 className="modal-title font-weight-normal">Add Multiple Petty Cash Entries</h5>
                                <button type="button" className="btn-close text-dark" aria-label="Close" onClick={handleBulkCloseModal} />
                            </div>
                            <form onSubmit={handleBulkSubmit}>
                                <div className="modal-body row">
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Branch *</label>
                                            <select
                                                name="bulkBranchId"
                                                className="form-control"
                                                value={bulkBranchId}
                                                onChange={(e) => setBulkBranchId(e.target.value)}
                                                required
                                            >
                                                <option value="">Select Branch</option>
                                                {branches.map(branch => (
                                                    <option key={branch.id} value={branch.id}>
                                                        {branch.branchName}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Project</label>
                                            <select
                                                name="bulkProjectId"
                                                className="form-control"
                                                value={bulkProjectId}
                                                onChange={(e) => setBulkProjectId(e.target.value)}
                                            >
                                                <option value="">Select Project</option>
                                                {projects.map(project => (
                                                    <option key={project.id} value={project.id}>
                                                        {project.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="col-12">
                                        <div className="table-responsive">
                                            <table className="table table-bordered align-items-center mb-0">
                                                <thead>
                                                    <tr>
                                                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7" style={{ minWidth: '120px' }}>Entity Type</th>
                                                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7" style={{ minWidth: '180px' }}>Select Entity</th>
                                                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7" style={{ minWidth: '150px' }}>Exp. Type</th>
                                                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7" style={{ minWidth: '130px' }}>Payment Date</th>
                                                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7" style={{ minWidth: '100px' }}>Amount (₹)</th>
                                                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7" style={{ minWidth: '120px' }}>Mode</th>
                                                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7" style={{ minWidth: '130px' }}>Tx No</th>
                                                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7" style={{ minWidth: '150px' }}>Remarks</th>
                                                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7" style={{ width: '50px' }}>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {bulkFormData.map((row, index) => (
                                                        <tr key={index}>
                                                            <td>
                                                                <select
                                                                    className="form-select form-select-sm"
                                                                    value={row.entityType}
                                                                    onChange={(e) => handleBulkInputChange(index, 'entityType', e.target.value)}
                                                                    required
                                                                >
                                                                    <option value="staff">Staff</option>
                                                                    <option value="member">Member</option>
                                                                    <option value="vendor">Vendor</option>
                                                                </select>
                                                            </td>
                                                            <td>
                                                                {row.entityType === 'staff' && (
                                                                    <select
                                                                        className="form-select form-select-sm"
                                                                        value={row.staffId}
                                                                        onChange={(e) => handleBulkInputChange(index, 'staffId', e.target.value)}
                                                                        required
                                                                    >
                                                                        <option value="">Select Staff</option>
                                                                        {staffs.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName} ({s.staffCode})</option>)}
                                                                    </select>
                                                                )}
                                                                {row.entityType === 'member' && (
                                                                    <select
                                                                        className="form-select form-select-sm"
                                                                        value={row.memberId}
                                                                        onChange={(e) => handleBulkInputChange(index, 'memberId', e.target.value)}
                                                                        required
                                                                    >
                                                                        <option value="">Select Member</option>
                                                                        {members.map(m => <option key={m.id} value={m.id}>{m.memberName}</option>)}
                                                                    </select>
                                                                )}
                                                                {row.entityType === 'vendor' && (
                                                                    <select
                                                                        className="form-select form-select-sm"
                                                                        value={row.vendorId}
                                                                        onChange={(e) => handleBulkInputChange(index, 'vendorId', e.target.value)}
                                                                        required
                                                                    >
                                                                        <option value="">Select Vendor</option>
                                                                        {vendors.map(v => <option key={v.id} value={v.id}>{v.vendorName}</option>)}
                                                                    </select>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <select
                                                                    className="form-select form-select-sm"
                                                                    value={row.expenditureTypeId}
                                                                    onChange={(e) => handleBulkInputChange(index, 'expenditureTypeId', e.target.value)}
                                                                    required
                                                                >
                                                                    <option value="">Select Type</option>
                                                                    {expenditureTypes.map(t => <option key={t.id} value={t.id}>{t.expenditureTypeName}</option>)}
                                                                </select>
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="date"
                                                                    className="form-control form-control-sm"
                                                                    value={row.paymentDate}
                                                                    onChange={(e) => handleBulkInputChange(index, 'paymentDate', e.target.value)}
                                                                    required
                                                                />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    className="form-control form-control-sm"
                                                                    placeholder="Amount"
                                                                    value={row.amount}
                                                                    onChange={(e) => handleBulkInputChange(index, 'amount', e.target.value)}
                                                                    required
                                                                />
                                                            </td>
                                                            <td>
                                                                <select
                                                                    className="form-select form-select-sm"
                                                                    value={row.paymentMode}
                                                                    onChange={(e) => handleBulkInputChange(index, 'paymentMode', e.target.value)}
                                                                >
                                                                    <option value="Cash">Cash</option>
                                                                    <option value="Bank Transfer">Bank Transfer</option>
                                                                    <option value="UPI">UPI</option>
                                                                    <option value="Cheque">Cheque</option>
                                                                </select>
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm"
                                                                    placeholder="Tx No"
                                                                    value={row.transactionNo}
                                                                    onChange={(e) => handleBulkInputChange(index, 'transactionNo', e.target.value)}
                                                                />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm"
                                                                    placeholder="Remarks"
                                                                    value={row.remarks}
                                                                    onChange={(e) => handleBulkInputChange(index, 'remarks', e.target.value)}
                                                                />
                                                            </td>
                                                            <td className="text-center">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-link text-danger mb-0 p-0"
                                                                    onClick={() => handleRemoveBulkRow(index)}
                                                                    disabled={bulkFormData.length === 1}
                                                                >
                                                                    <i className="material-symbols-rounded">delete</i>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary mt-3"
                                            onClick={handleAddBulkRow}
                                        >
                                            <i className="material-symbols-rounded text-sm">add_circle</i>&nbsp;Add Row
                                        </button>
                                    </div>
                                </div>
                                <div className="modal-footer border-0">
                                    <button type="button" className="btn bg-gradient-secondary" onClick={handleBulkCloseModal} disabled={saving}>Cancel</button>
                                    <button type="submit" className="btn bg-gradient-dark" disabled={saving}>
                                        {saving ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Saving...
                                            </>
                                        ) : (
                                            'Save All'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PettyCash;

